// src/hooks/useWordPress.ts
import { useState, useEffect, useCallback } from 'react';
import { WordPressPost, WordPressCategory, wordpressApi } from '../services/wordpressApi';

interface UsePostsParams {
    per_page?: number;
    page?: number;
    categories?: string;
    search?: string;
    orderby?: 'date' | 'title' | 'menu_order';
    order?: 'asc' | 'desc';
    autoLoad?: boolean;
}

interface UsePostsReturn {
    posts: WordPressPost[];
    loading: boolean;
    error: string | null;
    loadPosts: () => Promise<void>;
    loadMore: () => Promise<void>;
    hasMore: boolean;
    totalPages: number;
    currentPage: number;
}

export const usePosts = (params: UsePostsParams = {}): UsePostsReturn => {
    const [posts, setPosts] = useState<WordPressPost[]>([]);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const [hasMore, setHasMore] = useState(true);
    const [totalPages, setTotalPages] = useState(0);
    const [currentPage, setCurrentPage] = useState(params.page || 1);

    const {
        per_page = 10,
        categories,
        search,
        orderby = 'date',
        order = 'desc',
        autoLoad = true
    } = params;

    const loadPosts = useCallback(async (page: number = 1, append: boolean = false) => {
        try {
            setLoading(true);
            setError(null);

            const response = await fetch(`https://gmnbckend.gene6.ga/wp-json/wp/v2/posts?${new URLSearchParams({
                per_page: per_page.toString(),
                page: page.toString(),
                ...(categories && { categories }),
                ...(search && { search }),
                orderby,
                order
            })}`);

            if (!response.ok) {
                throw new Error(`Erreur HTTP: ${response.status}`);
            }

            const newPosts: WordPressPost[] = await response.json();
            const totalPages = parseInt(response.headers.get('X-WP-TotalPages') || '1');

            setTotalPages(totalPages);
            setCurrentPage(page);
            setHasMore(page < totalPages);

            if (append) {
                setPosts(prev => [...prev, ...newPosts]);
            } else {
                setPosts(newPosts);
            }
        } catch (err) {
            setError(err instanceof Error ? err.message : 'Erreur lors du chargement');
        } finally {
            setLoading(false);
        }
    }, [per_page, categories, search, orderby, order]);

    const loadMore = useCallback(async () => {
        if (hasMore && !loading) {
            await loadPosts(currentPage + 1, true);
        }
    }, [hasMore, loading, currentPage, loadPosts]);

    const refetch = useCallback(async () => {
        await loadPosts(1, false);
    }, [loadPosts]);

    useEffect(() => {
        if (autoLoad) {
            loadPosts(1, false);
        }
    }, [autoLoad, loadPosts]);

    return {
        posts,
        loading,
        error,
        loadPosts: refetch,
        loadMore,
        hasMore,
        totalPages,
        currentPage
    };
};

interface UseCategoriesReturn {
    categories: WordPressCategory[];
    loading: boolean;
    error: string | null;
    loadCategories: () => Promise<void>;
}

export const useCategories = (): UseCategoriesReturn => {
    const [categories, setCategories] = useState<WordPressCategory[]>([]);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState<string | null>(null);

    const loadCategories = useCallback(async () => {
        try {
            setLoading(true);
            setError(null);
            const fetchedCategories = await wordpressApi.getCategories();
            setCategories(fetchedCategories);
        } catch (err) {
            setError(err instanceof Error ? err.message : 'Erreur lors du chargement des catégories');
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => {
        loadCategories();
    }, [loadCategories]);

    return {
        categories,
        loading,
        error,
        loadCategories
    };
};

interface UsePostReturn {
    post: WordPressPost | null;
    loading: boolean;
    error: string | null;
    loadPost: (id: number) => Promise<void>;
}

export const usePost = (id?: number): UsePostReturn => {
    const [post, setPost] = useState<WordPressPost | null>(null);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState<string | null>(null);

    const loadPost = useCallback(async (postId: number) => {
        try {
            setLoading(true);
            setError(null);
            const fetchedPost = await wordpressApi.getPost(postId);
            setPost(fetchedPost);
        } catch (err) {
            setError(err instanceof Error ? err.message : 'Erreur lors du chargement de l\'article');
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => {
        if (id) {
            loadPost(id);
        }
    }, [id, loadPost]);

    return {
        post,
        loading,
        error,
        loadPost
    };
};

// Hook pour la recherche avec debounce
export const usePostSearch = (initialQuery: string = '', delay: number = 500) => {
    const [query, setQuery] = useState(initialQuery);
    const [debouncedQuery, setDebouncedQuery] = useState(initialQuery);

    useEffect(() => {
        const timer = setTimeout(() => {
            setDebouncedQuery(query);
        }, delay);

        return () => clearTimeout(timer);
    }, [query, delay]);

    const {
        posts,
        loading,
        error,
        loadPosts
    } = usePosts({
        search: debouncedQuery,
        autoLoad: false
    });

    useEffect(() => {
        if (debouncedQuery) {
            loadPosts();
        }
    }, [debouncedQuery, loadPosts]);

    return {
        query,
        setQuery,
        posts,
        loading,
        error,
        hasQuery: debouncedQuery.length > 0
    };
};
